/**
 * Case Renderer Component
 *
 * Main component for displaying and managing a clinical case masterclass.
 * Handles slide navigation, video playback, answer submission, and score calculation.
 *
 * Features:
 * - Slide-by-slide navigation with progress tracking
 * - Video integration (intro, outro, speaker videos)
 * - Question answering with score calculation
 * - Auto-reset on completed case reopening
 * - Anamnese and supporting documents panels
 * - Celebration modal on case completion
 *
 * @see src/modules/case/README.md for architecture details
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnamnesePanel } from '@/components/masterclass/AnamnesePanel';
import { SlideLayout } from '@/components/masterclass/SlideLayout';
import { SupportingDocumentsPanel } from '@/components/masterclass/SupportingDocumentsPanel';
import { ROUTES } from '@/config/routes';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { useTranslations } from '@/hooks/use-translations';
import { useIsAccreditationUser } from '@/hooks/useIsAccreditationUser';
import { supabase } from '@/integrations/supabase/client';
import { CelebrationModal } from '@/modules/celebration-modal';
import { DisclaimerOverlay } from '@/modules/disclaimer';
import { trackEvent } from '@/modules/google-analytics/analytic/analytics';
import { getCaseSlideIndexFromProgress } from '@/utils/getCaseSlideIndexFromProgress';
import { captureSentry } from '@/utils/sentryHelper';
import { isSlideQuestionSlide } from '@/utils/types/type-validation';
import { ValidationError } from '@/utils/types/validation';

import { SlideFactory } from './SlideFactory';
import { ValidationErrorDisplay } from './ValidationErrorDisplay';
import { useCaseData } from '../hooks/useCaseData';
import { useCasesConfig } from '../hooks/useCasesConfig';
import { SlideType } from '../types';
import {
  doesMasterClassQuestionHaveACorrectAnswer,
  isMasterClassAnswerCorrect,
} from '../utils/answer-validator';
import { getAssetById, getAssetUrl, getAssetsByRefs } from '../utils/asset-loader';
import { calculateScore } from '../utils/score-calculator';

interface CaseRendererProps {
  caseId: string; // This is the caseKey (e.g., "0024_IPSEN_spasticiteit")
}

export const CaseRenderer = ({ caseId }: CaseRendererProps) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { caseConfig, assetsBaseUrl, loading, error } = useCaseData(caseId);
  const { getCaseMetadataByKey } = useCasesConfig();

  const {
    caseProgress,
    updateCaseProgress,
    selectedCase,
    markCaseCompleted,
    markCelebrationShown,
    answers,
    recordAnswer,
    getAnswersForSlide,
  } = useMasterclass();
  const isAccreditationUser = useIsAccreditationUser();

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  const [videoEndAction, setVideoEndAction] = useState<'none' | 'next-slide' | 'close'>('none');
  const [isVideoComplete, setIsVideoComplete] = useState(true); // N'affecte plus la navigation
  const [isSpeakerVideoComplete, setIsSpeakerVideoComplete] = useState(true); // N'affecte plus la navigation
  const [hasAnsweredCorrectly, setHasAnsweredCorrectly] = useState(false);
  const [isAnamnesePanelOpen, setIsAnamnesePanelOpen] = useState(false);
  const [isSupportingDocsPanelOpen, setIsSupportingDocsPanelOpen] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [outroVideoForCelebration, setOutroVideoForCelebration] = useState<string | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  const hasInitialized = useRef(false);
  const progress = caseId ? caseProgress[caseId] : undefined;

  const handleDisclaimerComplete = () => {
    setShowDisclaimer(false);
  };

  useEffect(() => {
    if (!caseConfig) return;
    const currentSlide = caseConfig?.slides[currentSlideIndex];
    if (!isSlideQuestionSlide(currentSlide)) {
      setHasAnsweredCorrectly(false);
      return;
    }
    const correct = isMasterClassAnswerCorrect(
      caseConfig,
      currentSlide.id,
      answers.filter((a) => a.case_id === caseId).map((a) => a.answer_id)
    );
    setHasAnsweredCorrectly(correct);
  }, [answers, caseId, caseConfig, currentSlideIndex]);

  // Build answered questions map from answers array
  const slideIdToAnswerids: Map<string, string[]> = new Map();
  answers.forEach((answer) => {
    if (!slideIdToAnswerids.has(answer.slide_id)) {
      slideIdToAnswerids.set(answer.slide_id, [answer.answer_id]);
    } else {
      // Handle multiple answers for same slide
      const existing = slideIdToAnswerids.get(answer.slide_id);
      if (existing) {
        existing.push(answer.answer_id);
      }
    }
  });

  // NOTE: responsible for updating the current slide id
  useEffect(() => {
    if (caseConfig && caseId) {
      const currentSlide = caseConfig.slides[currentSlideIndex];
      if (currentSlide && caseProgress[caseId]?.currentSlideId !== currentSlide.id) {
        void updateCaseProgress(caseId, currentSlide.id);
      }
    }
  }, [currentSlideIndex, caseConfig, caseId, updateCaseProgress, caseProgress]);

  const handleSlideChange = useCallback(
    (newSlideIndex: number) => {
      if (!caseConfig) return;

      setCurrentSlideIndex(newSlideIndex);
      setHasAnsweredCorrectly(false);

      const slide = caseConfig.slides[newSlideIndex];
      const slideMetadata = caseConfig.masterclass?.slideMap[slide.id];

      // Note: isSpeakerVideoComplete n'affecte plus la navigation
      setIsSpeakerVideoComplete(true);

      // Note: isVideoComplete n'affecte plus la navigation
      setIsVideoComplete(true);

      // Check for intro video
      if (slideMetadata?.introVideoRef) {
        const videoAsset = getAssetById(slideMetadata.introVideoRef, caseConfig.assets);
        if (videoAsset) {
          setCurrentVideoUrl(getAssetUrl(videoAsset, assetsBaseUrl));
          setVideoEndAction('close');
          setIsVideoComplete(true);
        }
      } else {
        // If the new slide has no intro video, reset video state completely
        setCurrentVideoUrl(null);
        setIsVideoComplete(true);
      }
    },
    [caseConfig, assetsBaseUrl, progress]
  );

  // Initialize first slide with potential intro video (might not be the first slide if there was progress)
  useEffect(() => {
    if (caseConfig && currentSlideIndex === 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      handleSlideChange(getCaseSlideIndexFromProgress(caseConfig, caseProgress, caseId));
    }
  }, [caseConfig, currentSlideIndex, handleSlideChange]);

  const handleNext = () => {
    if (!caseConfig) return;

    const currentSlide = caseConfig.slides[currentSlideIndex];
    const slideMetadata = caseConfig.masterclass?.slideMap[currentSlide.id];

    // If on last slide, show celebration modal with outro video
    if (currentSlideIndex === caseConfig.slides.length - 1) {
      // Get outro video if exists
      let outroVideoUrl = null;
      if (slideMetadata?.outroVideoRef) {
        const videoAsset = getAssetById(slideMetadata.outroVideoRef, caseConfig.assets);
        if (videoAsset) {
          outroVideoUrl = getAssetUrl(videoAsset, assetsBaseUrl);
        }
      }

      // Open celebration modal with video
      setOutroVideoForCelebration(outroVideoUrl);
      if (caseId) {
        trackEvent('Case Progress', 'Finish Case', caseId, null);
        void markCaseCompleted(caseId);
        setShowCelebration(true);
      }
      return;
    }

    // Check for outro video on other slides
    if (slideMetadata?.outroVideoRef) {
      const videoAsset = getAssetById(slideMetadata.outroVideoRef, caseConfig.assets);
      if (videoAsset) {
        setCurrentVideoUrl(getAssetUrl(videoAsset, assetsBaseUrl));
        setVideoEndAction('next-slide');
        return;
      }
    }

    // Navigate to next slide
    if (currentSlideIndex < caseConfig.slides.length - 1) {
      handleSlideChange(currentSlideIndex + 1);
    }
  };

  const handleCelebrationClose = async () => {
    setShowCelebration(false);
    if (caseId) {
      await markCelebrationShown(caseId);
    }
    navigate(ROUTES.CASES);
  };

  /**
   * Handles answer selection and updates the case score.
   * Records all answers in the database, frontend computes correctness.
   */
  const handleAnswerSelected = async (answerId: string, isCorrect: boolean, videoRef?: string) => {
    if (caseConfig && caseId) {
      const { user } = (await supabase.auth.getUser()).data;
      if (user) {
        try {
          const currentSlide = caseConfig.slides[currentSlideIndex];
          const questionId = currentSlide.id;

          // Record answer (both correct and incorrect)
          await recordAnswer(caseId, questionId, answerId);

          // Only set hasAnswered to true for correct answers
          if (isCorrect) {
            setHasAnsweredCorrectly(true);

            // Recalculate score using all answers for this case
            const caseAnswers = answers.filter((el) => el.case_id === caseId);

            // Build answeredQuestions map from answers
            const answeredQuestionsMap: Record<string, string | string[]> = {};
            caseAnswers.forEach((answer) => {
              if (!answeredQuestionsMap[answer.slide_id]) {
                answeredQuestionsMap[answer.slide_id] = answer.answer_id;
              } else {
                const existing = answeredQuestionsMap[answer.slide_id];
                if (Array.isArray(existing)) {
                  existing.push(answer.answer_id);
                } else {
                  answeredQuestionsMap[answer.slide_id] = [existing, answer.answer_id];
                }
              }
            });

            // Add the new answer
            if (!answeredQuestionsMap[questionId]) {
              answeredQuestionsMap[questionId] = answerId;
            } else {
              const existing = answeredQuestionsMap[questionId];
              if (Array.isArray(existing)) {
                existing.push(answerId);
              } else {
                answeredQuestionsMap[questionId] = [existing, answerId];
              }
            }

            // Calculate score via utility function
            const { scorePercentage } = calculateScore(answeredQuestionsMap, caseConfig);

            // Save score to database
            await supabase
              .from('case_progress')
              .update({ score_percentage: scorePercentage })
              .eq('user_id', user.id)
              .eq('case_id', caseId);
          }
        } catch (error) {
          console.error('Error updating score:', error);
          captureSentry({
            title: 'Failed to update case score',
            error,
            detail: {
              caseId,
              slideId: currentSlide.id,
              answerId,
              userId: user.id,
            },
          });
        }
      }
    }

    if (!caseConfig || !videoRef) return;

    const videoAsset = getAssetById(videoRef, caseConfig.assets);

    if (videoAsset) {
      setCurrentVideoUrl(getAssetUrl(videoAsset, assetsBaseUrl));
      setVideoEndAction('close');
      setIsVideoComplete(false);
    }
  };

  const handleVideoEnd = () => {
    setIsVideoComplete(true);

    if (videoEndAction === 'next-slide') {
      if (currentSlideIndex < (caseConfig?.slides.length || 0) - 1) {
        handleSlideChange(currentSlideIndex + 1);
      } else {
        navigate(ROUTES.CASES);
      }
    }

    setCurrentVideoUrl(null);
    setVideoEndAction('none');
  };

  /** @deprecated N'affecte plus la navigation */
  const handleSpeakerVideoComplete = useCallback(() => {
    setIsSpeakerVideoComplete(true); // Conservé pour compatibilité
  }, []);

  const handleVideoReplay = useCallback((url: string) => {
    setCurrentVideoUrl(url);
    setVideoEndAction('close');
    setIsVideoComplete(false);
  }, []);

  const handleHomeClick = () => {
    navigate(ROUTES.CASES);
  };

  if (error) {
    // Si c'est une erreur de validation Zod, afficher les détails
    if (error instanceof ValidationError) {
      return <ValidationErrorDisplay error={error} caseKey={caseId} />;
    }

    // Sinon, affichage d'erreur générique
    const errorMessage = typeof error === 'string' ? error : error.message;
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">
            {t('common.messages.error', { message: errorMessage })}
          </p>
        </div>
      </div>
    );
  }

  if (loading || !caseConfig || !caseConfig.masterclass) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-muted-foreground">{t('common.messages.loadingCase')}</p>
        </div>
      </div>
    );
  }

  const currentSlide = caseConfig.slides[currentSlideIndex];
  const doesSlideHaveACorrectAnswer =
    isSlideQuestionSlide(currentSlide) &&
    doesMasterClassQuestionHaveACorrectAnswer(caseConfig, currentSlide.id);
  const requiresAnswer =
    isSlideQuestionSlide(currentSlide) &&
    (doesSlideHaveACorrectAnswer ||
      (!doesSlideHaveACorrectAnswer && getAnswersForSlide(caseId, currentSlide.id).length < 1));

  // Get anamnese data (first slide)
  const anamneseSlide = caseConfig.slides.find((s) => s.type === 'anamnese');
  const anamneseData = anamneseSlide?.items.find((item) => item.type === 'anamnese') || null;

  // Get supporting documents for current slide
  const supportingDocuments = getAssetsByRefs(currentSlide.libraryRefs, caseConfig.assets);

  // Show disclaimer overlay if not shown yet
  if (showDisclaimer && caseConfig?.disclaimer) {
    return (
      <DisclaimerOverlay
        disclaimer={caseConfig.disclaimer}
        assetsBaseUrl={assetsBaseUrl}
        onComplete={handleDisclaimerComplete}
      />
    );
  }

  return (
    <>
      {/* Anamnese Panel */}
      <AnamnesePanel
        isOpen={isAnamnesePanelOpen}
        onClose={() => setIsAnamnesePanelOpen(false)}
        anamneseData={anamneseData}
        allAssets={caseConfig.assets}
        assetsBaseUrl={assetsBaseUrl}
      />

      {/* Supporting Documents Panel */}
      <SupportingDocumentsPanel
        isOpen={isSupportingDocsPanelOpen}
        onClose={() => setIsSupportingDocsPanelOpen(false)}
        assets={supportingDocuments}
        assetsBaseUrl={assetsBaseUrl}
      />

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={showCelebration}
        onClose={() => void handleCelebrationClose()}
        videoUrl={outroVideoForCelebration || undefined}
      />

      <div className="h-full">
        <SlideLayout
          currentSlide={currentSlideIndex}
          totalSlides={caseConfig.slides.length}
          onSlideChange={handleSlideChange}
          onNext={handleNext}
          title={currentSlide.title}
          subtitle={caseConfig.displayName}
          caseNumber={caseId || '1'}
          onHomeClick={handleHomeClick}
          visitedSlideIds={progress?.visitedSlideIds || []}
          currentSlideId={progress?.currentSlideId || ''}
          slides={caseConfig.slides}
          videoUrl={currentVideoUrl}
          onVideoEnd={handleVideoEnd}
          onSpeakerVideoComplete={handleSpeakerVideoComplete}
          onVideoReplay={handleVideoReplay}
          isVideoComplete={isVideoComplete}
          isSpeakerVideoComplete={isSpeakerVideoComplete}
          requiresAnswer={requiresAnswer}
          hasAnsweredCorrectly={hasAnsweredCorrectly}
          onAnamneseClick={() => setIsAnamnesePanelOpen(true)}
          onDocsClick={() => setIsSupportingDocsPanelOpen(true)}
          docsCount={supportingDocuments.length}
          hideSpeakerVideo={showCelebration || currentSlide.type === SlideType.VIDEO}
          isAccreditationUser={isAccreditationUser}
          isCaseCompleted={progress?.completed || false}
        >
          <SlideFactory
            slide={currentSlide}
            assets={caseConfig.assets}
            assetsBaseUrl={assetsBaseUrl}
            masterClass={caseConfig.masterclass}
            onNext={handleNext}
            onPrevious={() => currentSlideIndex > 0 && setCurrentSlideIndex(currentSlideIndex - 1)}
            onAnswerSelected={handleAnswerSelected}
            answeredQuestions={slideIdToAnswerids}
            allAnswers={answers}
            caseConfig={caseConfig}
          />
        </SlideLayout>
      </div>
    </>
  );
};
